import { useMemo } from 'react';
import type { SignalWithRelatedIssuesCount } from '@momentum/types';
import { SignalCategory, SignalInstalledStatus } from '@momentum/types';
import { stripMentionMarkers } from '@/shared/utils/mention-parser';
import type { SignalFilters } from '../../constants';

export const useFilteredSignals = (
  allSignals: SignalWithRelatedIssuesCount[],
  filters: SignalFilters,
  currentUserId: string
): SignalWithRelatedIssuesCount[] =>
  useMemo(() => {
    let filtered = [...allSignals];

    if (filters.searchQuery.trim()) {
      const searchLower = filters.searchQuery.toLowerCase();
      filtered = filtered.filter((signal) =>
        stripMentionMarkers(signal.title ?? '')
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (filters.ownerFilter) {
      filtered = filtered.filter(
        (signal) => signal.createdBy === filters.ownerFilter
      );
    }

    if (filters.severityFilter !== null) {
      filtered = filtered.filter(
        (signal) => signal.severity === filters.severityFilter
      );
    }

    if (filters.statusFilter) {
      filtered = filtered.filter((signal) => {
        const userIsActive = signal.installed?.users?.some(
          (installedUser) =>
            installedUser.userId === currentUserId &&
            installedUser.status === SignalInstalledStatus.ACTIVE
        );
        return filters.statusFilter === 'active' ? userIsActive : !userIsActive;
      });
    }

    if (filters.categoryFilter) {
      filtered = filtered.filter((signal) => {
        const category = signal.category ?? SignalCategory.PLAYBOOK;
        return category === filters.categoryFilter;
      });
    }

    if (filters.sortBy === 'newest') {
      filtered.sort((signalA, signalB) => {
        const dateA = signalA.metadata?.createdAt
          ? new Date(signalA.metadata.createdAt).getTime()
          : 0;
        const dateB = signalB.metadata?.createdAt
          ? new Date(signalB.metadata.createdAt).getTime()
          : 0;
        return dateB - dateA;
      });
    } else if (filters.sortBy === 'severity') {
      filtered.sort((signalA, signalB) => signalB.severity - signalA.severity);
    }

    return filtered;
  }, [allSignals, filters, currentUserId]);
